import { useState, useEffect } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MONTHS, YEARS, BODY_TYPES } from "@/pages/NewJob";
import { INDIA_STATES, EMPTY_CUSTOMER } from "@/lib/constants";
import {
  User, Zap, Wrench, Plus, Trash2, Copy,
  CheckCircle2, Loader2, ChevronRight, ArrowRight, Camera
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Charger segment config ───────────────────────────────────────────────────
const CHARGER_SEGMENTS = [
  { label: "Small Charger",    icon: "🔋" },
  { label: "Fast Charger",     icon: "⚡" },
  { label: "Rickshaw Charger", icon: "🛺" },
];

const SEGMENT_MODELS = {
  "Small Charger":    ["54.6V / 6A","58.4V / 6A","67.2V / 6A","69.3V / 6A","71.2V / 6A","73V / 6A","84V / 5A"],
  "Fast Charger":     ["54.6V / 10A","58.4V / 10A","67.2V / 10A","69.3V / 10A","71.2V / 10A","73V / 10A","84V / 10A"],
  "Rickshaw Charger": ["54.6V / 15A","58.4V / 15A","67.2V / 15A","69.3V / 15A","71.2V / 15A","73V / 15A","54.6V / 20A","58.4V / 20A","67.2V / 20A","69.3V / 20A","71.2V / 20A","73V / 20A"],
};

const EMPTY_CHARGER = { charger_type: "", charger_spec: "", serial_number: "", purchase_month: "", purchase_year: "", body_type: "", photo_url: "" };

export default function CustomerRequest() {
  const [step,    setStep]    = useState("form");
  const [loading, setLoading] = useState(false);
  const [errors,  setErrors]  = useState({});
  const [result,  setResult]  = useState(null);

  const [customer, setCustomer] = useState({ ...EMPTY_CUSTOMER });
  const [chargers, setChargers] = useState([{ ...EMPTY_CHARGER }]);
  const [fault,    setFault]    = useState("");

  // Track visit
  useEffect(() => {
    axios.post(`${API}/analytics/event`, { page: "request", referrer: document.referrer }).catch(() => {});
  }, []);

  // ---- Charger helpers ----
  const addRow   = (n = 1) => setChargers(p => [...p, ...Array(n).fill(null).map(() => ({ ...EMPTY_CHARGER }))]);
  const removeRow = (i)    => setChargers(p => p.filter((_, idx) => idx !== i));
  const updateRow = (i, f, v) => setChargers(p => { const n = [...p]; n[i] = { ...n[i], [f]: v }; return n; });
  const duplicateRow = (i) => setChargers(p => { const c = { ...p[i] }; const n = [...p]; n.splice(i+1,0,c); return n; });

  // ---- Validation ----
  const validate = () => {
    const e = {};
    if (!customer.name.trim())  e.name  = "Your name is required";
    if (!customer.phone.trim()) e.phone = "Your phone number is required";
    if (!fault.trim())          e.fault = "Please describe the problem";
    chargers.forEach((c, i) => {
      if (!c.charger_type) e[`type_${i}`] = "Please select charger type";
      else if (!c.charger_spec) e[`spec_${i}`] = "Please select a model";
      if (!c.photo_url) e[`photo_${i}`] = "Photo is required — please upload a photo of the charger label";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ---- Photo upload per charger ----
  const [photoUploading, setPhotoUploading] = useState({});
  const handleChargerPhoto = async (i, file) => {
    if (!file) return;
    const MAX = 20 * 1024 * 1024;
    if (file.size > MAX) { alert(`Photo too large — maximum is 20 MB (yours: ${(file.size/1024/1024).toFixed(1)} MB)`); return; }
    setPhotoUploading(p => ({ ...p, [i]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload-photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateRow(i, "photo_url", r.data.url);
    } catch (err) { alert(err.response?.data?.detail || "Photo upload failed. Please try again."); }
    finally { setPhotoUploading(p => ({ ...p, [i]: false })); }
  };
  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const products = chargers.map(c => ({
        charger_type:  c.charger_type  || "",
        charger_spec:  c.charger_spec  || "",
        serial_number: c.serial_number || "",
        purchase_date: [c.purchase_month, c.purchase_year].filter(Boolean).join(" "),
        body_type:     c.body_type     || "",
        photo_url:     c.photo_url     || "",
      }));
      const res = await axios.post(`${API}/customer/request`, {
        customer,
        products,
        specs: { fault_description: fault, technical_notes: "", repair_notes: "" },
      });
      setResult(res.data);
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setErrors({ submit: err.response?.data?.detail || "Submission failed. Please try again." });
    } finally { setLoading(false); }
  };

  // ---- SUCCESS SCREEN ----
  if (step === "success" && result) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "linear-gradient(135deg,#f0fdf4 0%,#ffffff 60%,#f0fdf4 100%)" }}>
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
              Request Submitted!
            </h1>
            <p className="text-slate-500 text-sm mb-6">
              Your service request has been received. Our team will contact you shortly.
            </p>

            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
              <p className="text-xs text-green-700 font-semibold uppercase tracking-wide mb-2">Your Job Number</p>
              <p className="font-mono text-3xl font-bold text-green-700 tracking-tight">{result.job_no}</p>
              <p className="text-xs text-green-600 mt-2">Save this number to track your repair status</p>
            </div>

            <div className="space-y-3">
              <a href={`/track?job=${result.job_no}`}
                className="flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl font-semibold text-sm transition-colors">
                Track Your Repair <ArrowRight size={16} />
              </a>
              <button onClick={() => { setStep("form"); setResult(null); setCustomer({name:"",phone:"",address:""}); setChargers([{...EMPTY_CHARGER}]); setFault(""); }}
                className="w-full py-3 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-xl transition-colors">
                Submit Another Request
              </button>
            </div>
          </div>

          <p className="text-xs text-slate-400 mt-6">XSTRONG POWER EV CHARGER — Service Center</p>
        </div>
      </div>
    );
  }

  // ---- FORM ----
  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(160deg,#f0fdf4 0%,#ffffff 50%,#f0fdf4 100%)" }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.jpg" alt="XSTRONG POWER" className="h-10 w-auto object-contain" />
            <div>
              <p className="font-bold text-sm text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                XSTRONG POWER EV CHARGER
              </p>
              <p className="text-xs text-green-600 font-medium">Online Service Request</p>
            </div>
          </div>
          <a href="/track" className="text-xs text-slate-400 hover:text-green-700 transition-colors">
            Track existing job →
          </a>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-green-600 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Zap size={26} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Submit a Repair Request
          </h1>
          <p className="text-slate-500 text-sm">
            Fill in your details and charger information. We'll create a job card and contact you shortly.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center">
                <User size={17} className="text-white" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Your Details</h2>
                <p className="text-xs text-slate-400">Contact information so we can reach you</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-medium text-slate-700">Full Name <span className="text-rose-500">*</span></Label>
                  <Input
                    placeholder="e.g. Rahul Sharma"
                    value={customer.name}
                    onChange={e => setCustomer({ ...customer, name: e.target.value })}
                    className={`mt-1.5 h-10 ${errors.name ? "border-rose-400" : ""}`}
                  />
                  {errors.name && <p className="text-xs text-rose-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">Phone Number <span className="text-rose-500">*</span></Label>
                  <Input
                    placeholder="9876543210"
                    value={customer.phone}
                    onChange={e => setCustomer({ ...customer, phone: e.target.value })}
                    className={`mt-1.5 h-10 ${errors.phone ? "border-rose-400" : ""}`}
                    inputMode="numeric"
                  />
                  {errors.phone && <p className="text-xs text-rose-500 mt-1">{errors.phone}</p>}
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium text-slate-700">Alternate Phone Number</Label>
                <Input
                  placeholder="Alternative contact number (optional)"
                  value={customer.alt_phone}
                  onChange={e => setCustomer({ ...customer, alt_phone: e.target.value })}
                  className="mt-1.5 h-10"
                  inputMode="numeric"
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-slate-700">Street Address</Label>
                <Textarea
                  placeholder="House/Shop No., Street, Area"
                  value={customer.address}
                  onChange={e => setCustomer({ ...customer, address: e.target.value })}
                  className="mt-1.5 text-sm resize-none"
                  rows={2}
                />
              </div>

              <div>
                <Label className="text-xs font-medium text-slate-700">Landmark</Label>
                <Input
                  placeholder="e.g. Near Metro Station, Opposite City Mall"
                  value={customer.landmark}
                  onChange={e => setCustomer({ ...customer, landmark: e.target.value })}
                  className="mt-1.5 h-10"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-medium text-slate-700">City</Label>
                  <Input
                    placeholder="e.g. Mumbai"
                    value={customer.city}
                    onChange={e => setCustomer({ ...customer, city: e.target.value })}
                    className="mt-1.5 h-10"
                  />
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">State</Label>
                  <Select value={customer.state} onValueChange={v => setCustomer({ ...customer, state: v })}>
                    <SelectTrigger className="mt-1.5 h-10 text-sm">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {INDIA_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-medium text-slate-700">PIN Code</Label>
                  <Input
                    placeholder="400001"
                    value={customer.pincode}
                    onChange={e => setCustomer({ ...customer, pincode: e.target.value })}
                    className="mt-1.5 h-10 font-mono"
                    maxLength={6}
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Charger Details */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-green-600 flex items-center justify-center">
                  <Zap size={17} className="text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                    Charger Details
                  </h2>
                  <p className="text-xs text-slate-400">{chargers.length} charger{chargers.length !== 1 ? "s" : ""} — add as many as needed</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {chargers.map((c, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4 bg-slate-50/40 relative">
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-7 h-7 rounded-full bg-green-600 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                      <span className="text-sm font-semibold text-slate-700">Charger #{i + 1}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {chargers.length > 1 && (
                        <button type="button" onClick={() => duplicateRow(i)}
                          className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" title="Duplicate">
                          <Copy size={13} />
                        </button>
                      )}
                      {chargers.length > 1 && (
                        <button type="button" onClick={() => removeRow(i)}
                          className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors" title="Remove">
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Charger Type Segments */}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium text-slate-700">
                        Charger Type <span className="text-rose-500">*</span>
                      </Label>
                      <div className="flex gap-2 mt-1.5">
                        {CHARGER_SEGMENTS.map(seg => (
                          <button
                            key={seg.label}
                            type="button"
                            onClick={() => {
                              updateRow(i, "charger_type", seg.label);
                              updateRow(i, "charger_spec", "");
                            }}
                            className={`flex-1 py-2.5 px-2 rounded-xl text-xs font-semibold border transition-all leading-tight ${
                              c.charger_type === seg.label
                                ? "bg-green-600 text-white border-green-600 shadow-sm"
                                : "bg-white text-slate-600 border-slate-200 hover:border-green-400 hover:text-green-700 hover:bg-green-50"
                            }`}
                          >
                            <span className="block text-base mb-0.5">{seg.icon}</span>
                            {seg.label}
                          </button>
                        ))}
                      </div>
                      {errors[`type_${i}`] && (
                        <p className="text-xs text-rose-500 mt-1">{errors[`type_${i}`]}</p>
                      )}
                    </div>

                    {/* Model dropdown — shown only after segment is selected */}
                    {c.charger_type && (
                      <div className="sm:col-span-2">
                        <Label className="text-xs font-medium text-slate-700">
                          Select Model <span className="text-rose-500">*</span>
                        </Label>
                        <Select
                          value={c.charger_spec}
                          onValueChange={v => updateRow(i, "charger_spec", v)}
                        >
                          <SelectTrigger className={`mt-1.5 h-10 text-sm font-semibold ${errors[`spec_${i}`] ? "border-rose-400" : ""}`}>
                            <SelectValue placeholder={`Select ${c.charger_type} model…`} />
                          </SelectTrigger>
                          <SelectContent>
                            {(SEGMENT_MODELS[c.charger_type] || []).map(m => (
                              <SelectItem key={m} value={m}>
                                <span className="font-mono font-semibold text-green-700">{m}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors[`spec_${i}`] && (
                          <p className="text-xs text-rose-500 mt-0.5">{errors[`spec_${i}`]}</p>
                        )}
                        {c.charger_spec && (
                          <p className="text-xs text-slate-500 mt-1">
                            Selected: <span className="font-semibold text-green-700">{c.charger_type} — {c.charger_spec}</span>
                          </p>
                        )}
                      </div>
                    )}

                    {/* Serial */}
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Serial Number</Label>
                      <Input
                        placeholder="SN-XXXXXX (if available)"
                        value={c.serial_number}
                        onChange={e => updateRow(i, "serial_number", e.target.value)}
                        className="mt-1.5 h-9 text-sm font-mono"
                      />
                    </div>

                    {/* Body Type */}
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Body Type</Label>
                      <Select value={c.body_type} onValueChange={v => updateRow(i, "body_type", v)}>
                        <SelectTrigger className="mt-1.5 h-9 text-sm">
                          <SelectValue placeholder="Select body type" />
                        </SelectTrigger>
                        <SelectContent>
                          {BODY_TYPES.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Month */}
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Purchase Month</Label>
                      <Select value={c.purchase_month} onValueChange={v => updateRow(i, "purchase_month", v)}>
                        <SelectTrigger className="mt-1.5 h-9 text-sm">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent className="max-h-52">
                          {MONTHS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Year */}
                    <div>
                      <Label className="text-xs font-medium text-slate-700">Purchase Year</Label>
                      <Select value={c.purchase_year} onValueChange={v => updateRow(i, "purchase_year", v)}>
                        <SelectTrigger className="mt-1.5 h-9 text-sm">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent className="max-h-52">
                          {YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Photo upload */}
                    <div className="sm:col-span-2">
                      <Label className="text-xs font-medium text-slate-700">
                        Charger Photo <span className="text-rose-500">*</span>
                        <span className="text-slate-400 font-normal ml-1">(max 20 MB)</span>
                      </Label>
                      <div className="mt-1.5 flex items-start gap-3 flex-wrap">
                        {c.photo_url ? (
                          <div className="relative group">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${c.photo_url}`} alt="Charger"
                              className="w-20 h-20 object-cover rounded-xl border-2 border-green-200 shadow-sm" />
                            <button type="button" onClick={() => updateRow(i, "photo_url", "")}
                              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-rose-500 text-white rounded-full text-xs font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                          </div>
                        ) : null}
                        <label className="cursor-pointer">
                          <input type="file" accept="image/*" className="hidden"
                            onChange={e => handleChargerPhoto(i, e.target.files?.[0])} />
                          <div className={`h-10 px-4 flex items-center gap-2 rounded-xl border text-sm transition-colors ${
                            photoUploading[i]
                              ? "border-green-300 bg-green-50 text-green-600"
                              : errors[`photo_${i}`] && !c.photo_url
                              ? "border-rose-400 bg-rose-50 text-rose-600"
                              : "border-slate-200 bg-white text-slate-600 hover:border-green-400 hover:text-green-700"
                          }`}>
                            {photoUploading[i]
                              ? <><Loader2 size={14} className="animate-spin" /> Uploading…</>
                              : <><Camera size={14} /> {c.photo_url ? "Change Photo" : "Add Photo"}</>}
                          </div>
                        </label>
                      </div>
                      {errors[`photo_${i}`] && !c.photo_url && (
                        <p className="text-xs text-rose-500 mt-1">{errors[`photo_${i}`]}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add charger button */}
            <button type="button" onClick={() => addRow(1)}
              className="w-full mt-4 flex items-center justify-center gap-2 text-sm text-green-700 hover:text-green-800 border border-dashed border-green-300 hover:border-green-500 rounded-xl py-3 transition-colors bg-green-50/30 hover:bg-green-50">
              <Plus size={15} /> Add Another Charger
            </button>

            {/* Summary */}
            {chargers.length > 1 && (
              <div className="mt-4 p-3 bg-green-50 rounded-xl border border-green-200 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xs font-bold">{chargers.length}</span>
                </div>
                <p className="text-xs font-semibold text-green-800">
                  {chargers.length} chargers will be included in your service request
                </p>
              </div>
            )}
          </div>

          {/* Fault Description */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                <Wrench size={17} className="text-amber-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Problem Description</h2>
                <p className="text-xs text-slate-400">What's wrong with your charger?</p>
              </div>
            </div>
            <Textarea
              placeholder="Describe the issue (e.g. charger not charging, overheating, LED not working, strange sound...)"
              value={fault}
              onChange={e => setFault(e.target.value)}
              className={`text-sm resize-none ${errors.fault ? "border-rose-400" : ""}`}
              rows={4}
            />
            {errors.fault && <p className="text-xs text-rose-500 mt-1">{errors.fault}</p>}
          </div>

          {/* Error */}
          {errors.submit && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700">
              {errors.submit}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white py-4 rounded-xl text-base font-semibold transition-colors shadow-lg shadow-green-200"
            data-testid="customer-request-submit"
          >
            {loading
              ? <><Loader2 size={18} className="animate-spin" /> Submitting...</>
              : <><ChevronRight size={18} /> Submit Service Request — {chargers.length} Charger{chargers.length !== 1 ? "s" : ""}</>}
          </button>

          <p className="text-center text-xs text-slate-400">
            After submitting, you'll receive a <strong>Job Number</strong> to track your repair anytime at{" "}
            <a href="/track" className="text-green-600 hover:underline">our tracking page</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
